import { Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import type { ReactNode } from "react";
import { HeroSwirlContainer } from "src/components/HeroSwirlContainer/HeroSwirlContainer";
import { Logo } from "src/components/logo/Logo";
import { SignOutButton } from "./StatusPageActions";

interface StatusPageProps {
  /** Large decorative numeral, e.g. "404". */
  code?: string;
  title: string;
  body: ReactNode;
  /** Buttons from ./StatusPageActions. */
  actions?: ReactNode;
  /**
   * Shows Sign Out after the other actions; pages pass `useSignOut()`, or
   * `useSignInRecovery().signOut` when sign-in didn't complete. A prop, not
   * read here, because auth imports this layout via AccessDeniedPage.
   */
  onSignOut?: () => void;
}

/**
 * Full-page layout for states that replace the shell (access denied, sign-in
 * failed, not found, app error).
 * Reference: twosion-ui src/pages/PageNotFound/PageNotFound.page.tsx.
 */
export function StatusPage({ code, title, body, actions, onSignOut }: StatusPageProps) {
  const { palette, breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down("sm"));

  const numeral = code && (
    <Typography
      aria-hidden
      sx={{
        // info.light on background.brand: 5.98:1 light, 7.85:1 dark
        // (primary.light is only 3.60:1 in dark).
        color: palette.info.light,
        fontSize: { xs: 140, lg: 280 },
        lineHeight: 1,
      }}
    >
      {code}
    </Typography>
  );

  return (
    <Stack
      sx={{
        backgroundColor: palette.background.brand,
        position: "relative",
        overflow: "hidden",
        flex: 1,
        minHeight: "100vh",
      }}
    >
      <HeroSwirlContainer
        direction={isMobile ? "column" : "row"}
        width="100%"
        height="100%"
        flex={1}
        alignItems="center"
        justifyContent="space-around"
        alignSelf="center"
        pb="10%"
        px={isMobile ? 2 : 4}
        pt={isMobile ? 2 : 0}
      >
        <Stack gap={isMobile ? 3 : 4} alignItems={isMobile ? "center" : "flex-start"}>
          <Logo dark />
          {isMobile && numeral}
          <Typography
            variant={isMobile ? "h4" : "h3"}
            component="h1"
            fontWeight={isMobile ? 600 : 300}
            sx={{ color: palette.common.white }}
          >
            {title}
          </Typography>
          <Typography
            component="div"
            variant={isMobile ? "body2" : "body1"}
            textAlign={isMobile ? "center" : "left"}
            maxWidth={isMobile ? 300 : 463}
            sx={{ color: palette.common.white }}
          >
            {body}
          </Typography>
          {/* zIndex keeps the buttons above the wave graphic on short viewports */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            gap={2}
            width={isMobile ? "100%" : undefined}
            zIndex={1}
          >
            {actions}
            {onSignOut && (
              <SignOutButton emphasis={actions ? "secondary" : "primary"} onSignOut={onSignOut} />
            )}
          </Stack>
        </Stack>
        {!isMobile && numeral}
      </HeroSwirlContainer>
    </Stack>
  );
}
